import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBOfferIdeaGroup } from "@/lib/db-types";

export type IdeaGroupPatch = Partial<Pick<DBOfferIdeaGroup, "label" | "x" | "y" | "width" | "height">>;

const groupsKey = (offerId: string) => ["offer-idea-groups", offerId];

/** Gruppene på Idé-tavlen (VIB-2980), uten mykt slettede. */
export function useOfferIdeaGroups(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-idea-groups", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<DBOfferIdeaGroup[]> => {
      const { data, error } = await supabase
        .from("offer_idea_groups")
        .select("*")
        .eq("offer_id", offerId!)
        .is("deleted_at", null)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as DBOfferIdeaGroup[];
    },
  });
}

const fail = (what: string) => (err: unknown) =>
  toast.error(`Kunne ikke ${what}: ${err instanceof Error ? err.message : "ukjent feil"}`);

/** Mutasjoner for gruppene på ett tilbud. Aktør er den ekte innloggede brukeren. */
export function useOfferIdeaGroupMutations(offerId: string) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const actor = authUser?.id ?? null;
  const refresh = () => invalidateFor(qc, "offer_idea_groups", { offerId });

  const create = useMutation({
    mutationFn: async (fields: IdeaGroupPatch): Promise<DBOfferIdeaGroup> => {
      const { data, error } = await supabase
        .from("offer_idea_groups")
        .insert({ ...fields, offer_id: offerId, created_by: actor, updated_by: actor })
        .select()
        .single();
      if (error) throw error;
      return data as DBOfferIdeaGroup;
    },
    onSuccess: refresh,
    onError: fail("lage gruppen"),
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: IdeaGroupPatch }) => {
      const { error } = await supabase
        .from("offer_idea_groups")
        .update({ ...patch, updated_by: actor })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: ({ id, patch }) =>
      qc.setQueryData<DBOfferIdeaGroup[]>(groupsKey(offerId), (old) =>
        old?.map((g) => (g.id === id ? { ...g, ...patch } : g)),
      ),
    onSettled: refresh,
    onError: fail("lagre gruppen"),
  });

  /**
   * Myk sletting og angre. Kortene i gruppen blir liggende; et kort som
   * peker på en slettet gruppe vises som ugruppert til gruppen gjenopprettes.
   */
  const setDeleted = useMutation({
    mutationFn: async ({ id, deleted }: { id: string; deleted: boolean }) => {
      const { error } = await supabase
        .from("offer_idea_groups")
        .update({ deleted_at: deleted ? new Date().toISOString() : null, updated_by: actor })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: ({ id, deleted }) => {
      if (deleted) qc.setQueryData<DBOfferIdeaGroup[]>(groupsKey(offerId), (old) => old?.filter((g) => g.id !== id));
    },
    onSettled: refresh,
    onError: fail("slette gruppen"),
  });

  return { create, update, setDeleted };
}
